const DECISION_LINK_PREFIX = '#decision-';

/**
 * Normalize markdown text that may have headings, bullets, and list markers
 * inlined without newlines (common in LLM output).
 */
export function normalizeMarkdown(text: string): string {
  let result = text;

  // Insert newline before headings when the model inlines them mid-sentence.
  result = result.replace(/([^\n])(\s#{1,4}\s)/g, '$1\n$2');

  // Handle orphan heading markers where the markdown fence is on its own line.
  result = result.replace(/(^|\n)(#{1,4})\s*\n(?=\S)/g, '$1$2 ');

  // Insert newline before bullet markers (- or *) after sentence-ending punctuation.
  result = result.replace(/([.;:!?)\]])\s+([-*]\s)/g, '$1\n$2');

  // Insert newline before numbered list markers (1. 2. etc.) when mid-line.
  result = result.replace(/([.;:!?)\]])\s+(\d+[.)]\s)/g, '$1\n$2');

  // Insert newline before bold title patterns that act as headers.
  result = result.replace(/([.;:!?)\]])\s+(\*\*[^*]+\*\*)/g, '$1\n$2');

  return result.trim();
}

export function linkDecisionReferencesInMarkdown(markdown: string): string {
  const linkPlaceholders: string[] = [];
  const placeholderPrefix = '__GO_MD_LINK_';

  const protectedMarkdown = markdown.replace(/\[[^\]]+\]\([^)]+\)/g, (match) => {
    const placeholder = `${placeholderPrefix}${linkPlaceholders.length}__`;
    linkPlaceholders.push(match);
    return placeholder;
  });

  const linkedMarkdown = protectedMarkdown.replace(/\bDRN-\d{1,10}\b/gi, (match) => {
    const normalizedReference = match.toUpperCase();
    return `[${normalizedReference}](${DECISION_LINK_PREFIX}${normalizedReference})`;
  });

  return linkedMarkdown.replace(new RegExp(`${placeholderPrefix}(\\d+)__`, 'g'), (_, index) => {
    return linkPlaceholders[Number(index)] ?? '';
  });
}

export function preprocessReasoningMarkdown(markdown: string): string {
  return linkDecisionReferencesInMarkdown(normalizeMarkdown(markdown));
}

export function isDecisionLink(href?: string | null): boolean {
  return typeof href === 'string' && href.startsWith(DECISION_LINK_PREFIX);
}

export function getDecisionReferenceFromHref(href?: string | null): string | null {
  if (!isDecisionLink(href)) {
    return null;
  }

  return href!.slice(DECISION_LINK_PREFIX.length) || null;
}
